import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ConversationService } from './conversation.service';
import {
    NativeTwilioVoiceEvent,
    TwilioVoice,
} from '../plugins/twilio-voice.plugin';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';

export type NativeVoiceCallStatus =
    | 'idle'
    | 'ringing'
    | 'connecting'
    | 'in-progress'
    | 'on-hold'
    | 'reconnecting'
    | 'completed';

export interface NativeVoiceCallState {
    incomingCall: any | null;
    activeCallSid: string | null;
    displayedCallSid: string | null;
    callStatus: NativeVoiceCallStatus;
}

@Injectable({
    providedIn: 'root',
})
export class NativeTwilioVoiceService {
    private static readonly REGISTRATION_REFRESH_LEAD_MS = 5 * 60 * 1000;
    private static readonly REGISTRATION_REFRESH_RECHECK_MS = 60 * 1000;
    private syncing = false;
    private listenersReady = false;
    private listenerHandles: PluginListenerHandle[] = [];
    private lifecycleListenersReady = false;
    private lifecycleListenerHandles: PluginListenerHandle[] = [];
    private registrationRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
    private registrationExpiresAt = 0;
    private callUiSetupInFlight = false;
    private readonly stateSubject = new BehaviorSubject<NativeVoiceCallState>({
        incomingCall: null,
        activeCallSid: null,
        displayedCallSid: null,
        callStatus: 'idle',
    });

    readonly state$ = this.stateSubject.asObservable();

    constructor(
        private conversationService: ConversationService,
        private afAuth: AngularFireAuth,
        private userService: UserService
    ) {
        void this.initListeners();
        void this.initLifecycleListeners();
    }

    isNativeAndroid(): boolean {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }

    isNativeVoiceSupported(): boolean {
    return Capacitor.isNativePlatform() &&
        (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios');
    }


    getSnapshot(): NativeVoiceCallState {
        return this.stateSubject.value;
    }

    private resolveEventCallSid(
        event: NativeTwilioVoiceEvent | null | undefined,
        current: NativeVoiceCallState
    ): string | null {
        if (event && Object.prototype.hasOwnProperty.call(event, 'callSid')) {
            return event.callSid ?? null;
        }

        return current.activeCallSid || current.incomingCall?.call_sid || null;
    }

    private resolveEventDisplayedCallSid(
        event: NativeTwilioVoiceEvent | null | undefined,
        current: NativeVoiceCallState
    ): string | null {
        if (event && Object.prototype.hasOwnProperty.call(event, 'displayedCallSid')) {
            return event.displayedCallSid ?? null;
        }

        return current.displayedCallSid || null;
    }

    async syncRegistration(): Promise<void> {
        if (!this.isNativeVoiceSupported() || this.syncing) {
            return;
        }

        if (this.isNativeAndroid()) {
            await this.ensureSystemCallUiSetup();
        }

        const lastRegistrationAt = Number(localStorage.getItem('connect_voice_registration_at') || 0);
        if (lastRegistrationAt && Date.now() - lastRegistrationAt < 30 * 60 * 1000) {
            return;
        }

        this.syncing = true;
        try {
            await this.initListeners();
            await this.ensurePermissions();
            await this.configureSession();
            const tokenResp = await this.conversationService.getVoiceToken().toPromise();
            await TwilioVoice.register({
                accessToken: tokenResp.token,
                identity: tokenResp.identity,
                expiresIn: tokenResp.expires_in,
            });
            localStorage.setItem('connect_voice_registration_at', Date.now().toString());
            this.scheduleRegistrationRefresh(tokenResp.expires_in);
        } catch (error) {
            console.error('Failed to register native Twilio voice device:', error);
        } finally {
            this.syncing = false;
        }
    }

    async unregister(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        this.clearRegistrationRefresh();

        try {
            await TwilioVoice.unregister();
            this.resetState();
        } catch (error) {
            console.error('Failed to unregister native Twilio voice device:', error);
        }
    }

    async answerIncomingCall(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await this.syncRegistration();
        await this.ensurePermissions();

        const current = this.stateSubject.value;
        this.stateSubject.next({
            ...current,
            activeCallSid:
                current.activeCallSid || current.incomingCall?.call_sid || current.incomingCall?.callSid || null,
            callStatus: 'connecting',
        });

        await TwilioVoice.answerIncomingCall();
    }

    async startOutgoingCall(options: {
        to: string;
        from: string;
        conversationId?: string | number | null;
        customerId?: string | number | null;
        dealerId?: string | number | null;
        agentId?: string | number | null;
        contactName?: string | null;
    }): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await this.syncRegistration();
        await this.ensurePermissions();

        const current = this.stateSubject.value;
        this.stateSubject.next({
            incomingCall: null,
            activeCallSid: null,
            displayedCallSid: current.displayedCallSid,
            callStatus: 'connecting',
        });

        await TwilioVoice.startOutgoingCall(options);
    }

    async rejectIncomingCall(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await TwilioVoice.rejectIncomingCall();
        this.resetState('completed');
    }

    async disconnectActiveCall(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await TwilioVoice.disconnectActiveCall();
    }

    async openCallScreen(options: {
        callerName?: string | null;
        callerNumber?: string | null;
        conversationId?: string | number | null;
        callSid?: string | null;
        dealerId?: string | number | null;
        toNumber?: string | null;
        callStatus?: string | null;
        inCallMode?: boolean;
    }): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await TwilioVoice.openCallScreen({
            callerName: options.callerName,
            callerNumber: options.callerNumber,
            conversationId: options.conversationId,
            callSid: options.callSid,
            dealerId: options.dealerId,
            toNumber: options.toNumber,
            callStatus: options.callStatus,
            inCallMode: options.inCallMode ?? true,
        });
    }

    async refreshCurrentState(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        const snapshot = await TwilioVoice.getCallState();
        const current = this.stateSubject.value;
        const nextCallStatus =
            (snapshot?.callStatus as NativeVoiceCallStatus) || current.callStatus;
        if (nextCallStatus === 'completed' || nextCallStatus === 'idle') {
            this.stateSubject.next({
                incomingCall: null,
                activeCallSid: null,
                displayedCallSid: null,
                callStatus: nextCallStatus,
            });
            return;
        }

        this.stateSubject.next({
            incomingCall: current.incomingCall,
            activeCallSid:
                snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'callSid')
                    ? snapshot.callSid ?? null
                    : current.activeCallSid || null,
            displayedCallSid:
                snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'displayedCallSid')
                    ? snapshot.displayedCallSid ?? null
                    : current.displayedCallSid || null,
            callStatus: nextCallStatus,
        });
    }

    async ensurePermissions(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await TwilioVoice.ensurePermissions();
    }

    async setMuted(muted: boolean): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await TwilioVoice.setMuted({ muted });
    }

    async setSpeakerEnabled(enabled: boolean): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        await TwilioVoice.setSpeakerEnabled({ enabled });
    }

    private async configureSession(): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        const authToken = await this.afAuth.idToken
            .pipe(
                filter((token): token is string => !!token),
                take(1)
            )
            .toPromise();

        const userToken = this.userService?.user?.token;
        if (!userToken) {
            throw new Error('Backend user token is not available for native voice session.');
        }

        await TwilioVoice.configureSession({
            apiUrl: environment.api_url,
            appToken: environment.app_token,
            authToken,
            userToken,
        });
    }

    private async initLifecycleListeners(): Promise<void> {
        if (!this.isNativeVoiceSupported() || this.lifecycleListenersReady) {
            return;
        }

        this.lifecycleListenersReady = true;

        this.lifecycleListenerHandles = await Promise.all([
            CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                if (!isActive) {
                    return;
                }

                void this.ensureSystemCallUiSetup();
                void this.refreshRegistrationIfNeeded('appStateChange');
            }),
            CapacitorApp.addListener('resume', () => {
                void this.ensureSystemCallUiSetup();
                void this.refreshRegistrationIfNeeded('resume');
            }),
        ]);
    }

    async ensureSystemCallUiSetup(): Promise<void> {
        if (!this.isNativeAndroid() || this.callUiSetupInFlight) {
            return;
        }

        this.callUiSetupInFlight = true;
        try {
            await TwilioVoice.ensureSystemCallUi();
        } catch (error) {
            console.error('Unable to check Android calling-account setup:', error);
        } finally {
            this.callUiSetupInFlight = false;
        }
    }

    private scheduleRegistrationRefresh(expiresInSeconds?: number): void {
        const expiresInMs = Math.max(expiresInSeconds ?? 0, 60) * 1000;
        const refreshLeadMs = Math.min(
            NativeTwilioVoiceService.REGISTRATION_REFRESH_LEAD_MS,
            Math.max(30 * 1000, Math.floor(expiresInMs * 0.2))
        );
        const refreshInMs = Math.max(30 * 1000, expiresInMs - refreshLeadMs);

        this.clearRegistrationRefresh();
        this.registrationExpiresAt = Date.now() + expiresInMs;
        this.registrationRefreshTimeout = setTimeout(() => {
            void this.refreshRegistrationIfNeeded('scheduled');
        }, refreshInMs);
    }

    private clearRegistrationRefresh(): void {
        if (this.registrationRefreshTimeout) {
            clearTimeout(this.registrationRefreshTimeout);
            this.registrationRefreshTimeout = null;
        }

        this.registrationExpiresAt = 0;
    }

    private isRegistrationRefreshDue(): boolean {
        if (!this.registrationExpiresAt) {
            return true;
        }

        return (
            Date.now() + NativeTwilioVoiceService.REGISTRATION_REFRESH_RECHECK_MS >=
            this.registrationExpiresAt
        );
    }

    private async refreshRegistrationIfNeeded(reason: string): Promise<void> {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        if (!this.userService?.user?.token) {
            return;
        }

        if (!this.isRegistrationRefreshDue()) {
            return;
        }

        console.log(`Refreshing native Twilio voice registration (${reason})`);
        await this.syncRegistration();
    }

    markAnswerRequested(metadata?: {
        callSid?: string | null;
        conversationId?: string | number | null;
    }): void {
        if (!this.isNativeVoiceSupported()) {
            return;
        }

        const current = this.stateSubject.value;
        const mergedIncoming = {
            ...(current.incomingCall || {}),
            call_sid: metadata?.callSid || current.incomingCall?.call_sid || null,
            conversation_id:
                metadata?.conversationId || current.incomingCall?.conversation_id || null,
        };

        this.stateSubject.next({
            incomingCall: mergedIncoming,
            activeCallSid:
                metadata?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
            displayedCallSid: current.displayedCallSid,
            callStatus: 'connecting',
        });
    }

    private async initListeners(): Promise<void> {
        if (!this.isNativeVoiceSupported() || this.listenersReady) {
            return;
        }

        this.listenersReady = true;

        this.listenerHandles = await Promise.all([
            TwilioVoice.addListener('callInvite', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    ...current,
                    incomingCall: this.normalizeIncomingCall(event, current.incomingCall),
                    activeCallSid: current.activeCallSid,
                    displayedCallSid: current.displayedCallSid,
                    callStatus: 'ringing',
                });
            }),
            TwilioVoice.addListener('callConnecting', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid: this.resolveEventCallSid(event, current),
                    displayedCallSid: this.resolveEventDisplayedCallSid(event, current),
                    callStatus: 'connecting',
                });
            }),
            TwilioVoice.addListener('callRinging', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid: this.resolveEventCallSid(event, current),
                    displayedCallSid: this.resolveEventDisplayedCallSid(event, current),
                    callStatus: 'connecting',
                });
            }),
            TwilioVoice.addListener('callConnected', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid: this.resolveEventCallSid(event, current),
                    displayedCallSid: this.resolveEventDisplayedCallSid(event, current),
                    callStatus: 'in-progress',
                });
            }),
            TwilioVoice.addListener('callReconnecting', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid: this.resolveEventCallSid(event, current),
                    displayedCallSid: this.resolveEventDisplayedCallSid(event, current),
                    callStatus: 'reconnecting',
                });
            }),
            TwilioVoice.addListener('callReconnected', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid: this.resolveEventCallSid(event, current),
                    displayedCallSid: this.resolveEventDisplayedCallSid(event, current),
                    callStatus: 'in-progress',
                });
            }),
            TwilioVoice.addListener('callStateChanged', (event) => {
                const current = this.stateSubject.value;
                const nextCallStatus =
                    (event?.callStatus as NativeVoiceCallStatus) || current.callStatus;
                if (nextCallStatus === 'completed' || nextCallStatus === 'idle') {
                    this.stateSubject.next({
                        incomingCall: null,
                        activeCallSid: null,
                        displayedCallSid: null,
                        callStatus: nextCallStatus,
                    });
                    return;
                }

                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid: this.resolveEventCallSid(event, current),
                    displayedCallSid: this.resolveEventDisplayedCallSid(event, current),
                    callStatus: nextCallStatus,
                });
            }),
            TwilioVoice.addListener('callDisconnected', () => {
                this.resetState('completed');
            }),
            TwilioVoice.addListener('callCancelled', () => {
                this.resetState('completed');
            }),
            TwilioVoice.addListener('callConnectFailure', () => {
                this.resetState('completed');
            }),
            TwilioVoice.addListener('registrationError', (event) => {
                console.error('Native Twilio registration error:', event?.message || event);
            }),
        ]);
    }

    private normalizeIncomingCall(
        event: NativeTwilioVoiceEvent,
        existing: any | null
    ): any {
        return {
            ...(existing || {}),
            call_sid: event?.callSid || existing?.call_sid || null,
            conversation_id:
                event?.conversationId ?? existing?.conversation_id ?? null,
            from_number: event?.from || existing?.from_number || '',
            caller_name: event?.callerName || existing?.caller_name || 'Incoming caller',
            dealer_id: event?.dealerId ?? existing?.dealer_id ?? null,
            to_number: event?.toNumber || existing?.to_number || null,
        };
    }

    private resetState(callStatus: NativeVoiceCallStatus = 'idle'): void {
        this.stateSubject.next({
            incomingCall: null,
            activeCallSid: null,
            displayedCallSid: null,
            callStatus,
        });
    }
}
